import type { ImageLoaderProps } from "next/dist/shared/lib/image-config";
import { IPageLink } from "@/sanity/globals/CTA";
import { IPopup } from "@/sanity/globals/Popups";
import { PortableTextBlock } from "sanity";

export interface GROQImage {
  src: string;
  width: number;
  height: number;
  lqip: string;
  name: string;
}

export interface GROQCTA {
  name: string;
  title?: string;
  icon?: string;
  action: "link" | "popup" | "anchor";
  link?: IPageLink;
  popup?: IPopup;
  anchor?: string;
}

export interface GROQButton {
  cta?: GROQCTA;
}

export interface GROQCornerButton {
  type?: "default" | "dark";
  cta?: GROQCTA;
}

export interface IHeader {
  name: string;
  menu: {
    title: string;
    link?: IPageLink;
    isCategory: boolean;
    subItems?: {
      title: string;
      description?: string;
      icon?: GROQImage;
      link: IPageLink;
    }[];
    socialSubItems?: {
      image: GROQImage;
      title: string;
      link: IPageLink;
    }[];
  }[];
  headerLoginCTA: GROQButton;
  headerCTA: GROQButton;
}

export interface IFooter {
  navColumns: {
    heading: string;
    links: {
      title: string;
      link: IPageLink;
    }[];
  }[];
  poweredByCard: {
    cardLabel: string;
    headline: string;
    subtext: string;
  };
  copyright: string;
  legalLinks: {
    title: string;
    link: IPageLink;
  }[];
}

export interface ITrustpilotTestimonial {
  name: string;
  testimonials: {
    image: GROQImage;
    name: string;
    position: string;
    text: PortableTextBlock[];
  }[];
}

export interface ITraders {
  name: string;
  items: {
    image: GROQImage;
    text: PortableTextBlock[];
  }[];
}

export interface GROQMuxVideo {
  playbackId: string;
  assetId: string;
  filename: string;
  status: string;
}

export function GROQImageLoader({
  src,
  width,
  quality = 90,
}: ImageLoaderProps): string {
  return src + "?auto=format&q=" + quality;
}

export function GROQUnoptimizedImageLoader({
  src,
  width,
  quality = 75,
}: ImageLoaderProps): string {
  return src + "?q=100";
}

export const GROQImageSchema =
  '{"src": asset->url, "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height, "lqip": asset->metadata.lqip, "name": asset->originalFilename}';

export const GROQPageLinkSchema =
  "{link,externalLink,openInNewTab,url,anchor,internalLink->{settings{slug{current}}}}";

export const HubSpotFormSchema = `
{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    portalId,
    formId,
  }
}
`;

export const ZapierFormSchema = `
{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    url,
  }
}
`;

export const GROQPopupSchemaQuery = `{
  name,
  theme,
  chip,
  content,
  formType,
  hsForm->${HubSpotFormSchema},
  zapierForm->${ZapierFormSchema},
}`;

export const GROQFormSchema = `{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    portalId,
    formId,
  }
}`;

export const GROQPageCTASchema = `{
  name,
  title,
  icon,
  action,
  anchor,
  link${GROQPageLinkSchema},
  popup->${GROQPopupSchemaQuery}
}`;

export const GROQButtonSchema = `{
  cta->${GROQPageCTASchema}
}`;

export const GROQCornerButtonSchema = `{
  type,
  cta->${GROQPageCTASchema}
}`;

export const GROQMuxVideoShema = `{
  "playbackId": asset->playbackId,
  "assetId": asset->assetId,
  "filename": asset->filename,
  "status": asset->status
}`;

export const MuxVideoPlayerSchema = `{
  embed,
  muxVideo${GROQMuxVideoShema},
  embedUrl,
  thumbnail${GROQImageSchema}
}`;

export const HeaderSchema = `->{
  name,
  menu[]{
    title,
    link${GROQPageLinkSchema},
    isCategory,
    subItems[]{
      title,
      description,
      icon${GROQImageSchema},
      link${GROQPageLinkSchema},
    },
    socialSubItems[]{
      image${GROQImageSchema},
      title,
      link${GROQPageLinkSchema},
    },
  },
  headerLoginCTA${GROQButtonSchema},
  headerCTA${GROQButtonSchema},
}`;

export const FooterSchema = `->{
  navColumns[]{
    heading,
    links[]{
      title,
      link${GROQPageLinkSchema},
    },
  },
  poweredByCard{
    cardLabel,
    headline,
    subtext,
  },
  copyright,
  legalLinks[]{
    title,
    link${GROQPageLinkSchema},
  },
}`;

export const TrustpilotTestimonialsSchema = `{
  name,
  testimonials[]{
    image${GROQImageSchema},
    name,
    position,
    text,
  },
}`;

export const TradersSchema = `{
  name,
  items[]{
    image${GROQImageSchema},
    text,
  },
}`;

export interface IReview {
  _id?: string;
  title?: string;
  quote?: PortableTextBlock[];
  name?: string;
  rating?: number;
}

export const ReviewSchema = `{
  _id,
  title,
  quote,
  name,
  rating,
}`;

export interface IAbTestVariant {
  _key?: string;
  variantKey: string;
  page: {
    _id: string;
    _type: string;
  };
}

export interface IAbTest {
  enabled?: boolean;
  posthogFlagKey?: string;
  variants?: IAbTestVariant[];
}

export const GROQAbTestSchema = `abTest{
  enabled,
  posthogFlagKey,
  variants[]{
    _key,
    variantKey,
    "page": page->{ _id, _type },
  },
}`;
